/**
 * Implements a wallet trades table
 */
/* eslint-disable array-bracket-newline */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAccount } from 'wagmi';
import { IDexTrade } from '@kattana-io/common';
import Text from '../../../atoms/Text';
import Button from '../../../molecules/Button';
import TradesTable from './Table';
import { actionShowModal } from '../../../actions/modal';
import { ModalTypes } from '../../../reducers/modal';
import { WalletAPI } from '../../../store/createStore';
import Slider from '../../../molecules/Slider';

import alarm from '../../../assets/images/alarm.svg';

import './styles.scss';

const TRADES_TABLE_PAGE_OFFSET = 11;

interface ITradesProps {
  loading: boolean;
  tradesCount?: number;
}

const Trades = ({ loading, tradesCount }: ITradesProps) => {
  const params = useParams();
  const { address: ownAddress } = useAccount();

  const [page, setPage] = useState(1);
  const [loadingTrades, setLoadingTrades] = useState(true);
  const [trades, setTrades] = useState<IDexTrade[]>([]);

  const dispatch = useDispatch();

  const chain = params.chain || '';
  const address = params.address;

  useEffect(() => {
    setPage(1);
  }, [chain, address]);

  useEffect(() => {
    const controller = new AbortController();

    if (address && chain && (page === 1 || trades.length)) {
      setLoadingTrades(true);
      const before =
        page === 1
          ? Math.round(new Date().getTime() / 1000)
          : trades[trades.length - 1].timestamp;
      WalletAPI.walletAllDexTrades(
        chain,
        address,
        { before, size: TRADES_TABLE_PAGE_OFFSET },
        controller
      ).then((data) => {
        if (!controller.signal.aborted) {
          if (data) {
            setTrades(data);
          } else {
            setTrades([]);
          }
          setLoadingTrades(false);
        }
      });
    }
    return () => controller.abort();
  }, [address, chain, page]);

  const openMyTrades = () => {
    dispatch(
      actionShowModal(ModalTypes.myTrades, { chain, address: ownAddress })
    );
  };

  const onSetPage = (val: number) => {
    if (val < page) {
      setPage(1);
    } else {
      setPage(val);
    }
  };

  const maxPage = Math.ceil((tradesCount || 0) / TRADES_TABLE_PAGE_OFFSET);

  return (
    <div className='trades'>
      <div className='trades__header'>
        <div className='trades__header__tabs'>
          <Text size={16} className='trades__header__tabs--active'>
            History
          </Text>
        </div>
        <Button
          action={openMyTrades}
          size='s'
          variant='primary'
          text='My Trades'
          left_image={alarm}
          disabled={!ownAddress || !chain}
        />
      </div>
      <div className='trades__table'>
        <TradesTable chain={chain} trades={trades} loading={loadingTrades} />
      </div>
      <Slider
        page={page}
        setPage={onSetPage}
        isFinal={
          !maxPage || loading || trades.length < TRADES_TABLE_PAGE_OFFSET
        }
      />
    </div>
  );
};

export default Trades;
